import { ProvidesKey } from 'app/model/client/CORE';
import {
    Aerodrome,
    Airspace,
    AirspaceRef,
    AtcConfigOpening,
    DeltaAtfcmSituationRegulationDto,
    FlightAirspaceCrossing,
    FlightDto,
    FlightId,
    FlightPointDto,
    FlightRegLocation,
    MapHotspot,
    MapRegulationSituation,
    MeasureDto,
    MonitoredTv,
    NavPoint,
    Route,
    ScenarioAttributesDto,
    SimulationDto,
    Slot,
    TrafficVolume,
} from 'app/model/CORE';
import Utils from 'app/common/util/Utils';
import { MonitoredEvent, MonitoredMeasure, MonitoredRegulationOutlook } from 'app/model/SOL49';
import FlightProfileUtils from './FlightProfileUtils';
import hash from 'object-hash';

export type KeyCompartor = (o1: any, o2: any) => boolean;
export default class KeyUtils {
    static readonly AIRSPACE_KEY_PROVIDER = new ProvidesKey<Airspace>(item => item.reference.id);
    static readonly AIRSPACE_REF_KEY_PROVIDER = new ProvidesKey<AirspaceRef>(item => item.id + item.type);
    static readonly FLIGHT_KEY_PROVIDER = new ProvidesKey<FlightDto>(item => item.flightId);
    static readonly FLIGHT_ID_KEY_PROVIDER = new ProvidesKey<FlightId>(item => item.id || hash(item.flightKeys));
    //    static readonly REGULATION_KEY_PROVIDER = new ProvidesKey<RegulationDto>(item => item.regulId);
    //    static readonly REGULATION_PROPOSAL_KEY_PROVIDER = new ProvidesKey<RegulationProposalDto>(item=> item.regulId);
    static readonly SCENARIO_KEY_PROVIDER = new ProvidesKey<ScenarioAttributesDto>(item => item.scenarioId);
    static readonly MEASURE_KEY_PROVIDER = new ProvidesKey<MeasureDto>(item => item.measureId);
    static readonly FLIGHT_REG_LOCATION_KEY_PROVIDER = new ProvidesKey<FlightRegLocation>(item => item.regulationId);
    static readonly DELTA_REGULATION_KEY_PROVIDER = new ProvidesKey<DeltaAtfcmSituationRegulationDto>(
        item => item.afterSituation.regulId,
    );

    static readonly TV_KEY_PROVIDER = new ProvidesKey<TrafficVolume>(item => item.id);
    static readonly SIMULATION_KEY_PROVIDER = new ProvidesKey<SimulationDto>(item => item.dataset.simulIdentifier);
    static readonly FLIGHT_POINT_PROVIDER = new ProvidesKey<FlightPointDto>(item =>
        FlightProfileUtils.hasName(item) ? FlightProfileUtils.getName(item) : item.timeOver,
    );
    static readonly FLIGHT_AIRSPACE_PROVIDER = new ProvidesKey<FlightAirspaceCrossing>(
        item => item.airspaceId + item.firstEntryTime.getTime(),
    );
    static readonly AERODROME_PROVIDER = new ProvidesKey<Aerodrome>(item => item.icaoId);
    static readonly NAV_POINT_PROVIDER = new ProvidesKey<NavPoint>(item => item.id);
    static readonly ATC_CONFIG_OPENING_PROVIDER = new ProvidesKey<AtcConfigOpening>((item: AtcConfigOpening) => {
        return item.configId;
    });
    static readonly MAP_REGULATION_SITUATION_PROVIDER = new ProvidesKey<MapRegulationSituation>(
        (item: MapRegulationSituation) => {
            return item.regulation.regulId;
        },
    );
    static readonly MAP_HOTSPOT_PROVIDER = new ProvidesKey<MapHotspot>((item: MapHotspot) => {
        return item.hotspotDto.hotspotId;
    });
    static readonly ROUTE_PROVIDER = new ProvidesKey<Route>((item: Route) => {
        return item.id;
    });

    static readonly MONITORED_TV_KEY_PROVIDER = new ProvidesKey<MonitoredTv>(item => item.refLocation.id);
    static readonly MONITORED_MEASURE_KEY_PROVIDER = new ProvidesKey<MonitoredMeasure>(item => item.measure.measureId);
    static readonly MONITORED_REG_OUTLOOK_KEY_PROVIDER = new ProvidesKey<MonitoredRegulationOutlook>(
        item => item.regulationOutlook.aerodromeId,
    );

    static readonly SLOT_KEY_PROVIDER = new ProvidesKey<Slot>(item => item.referenceTime);

    static readonly MONITORED_EVENT_KEY_PROVIDER = new ProvidesKey<MonitoredEvent>(item => item.id);

    static isKeyEquals<T>(list1: T[], list2: T[], keyProvider: ProvidesKey<T>): boolean {
        if (list1 === list2) return true;
        if (!list1 || !list2) return false;
        if (list1.length !== list2.length) return false;

        for (let idx = 0; idx < list1.length; idx++) {
            if (Utils.isNotEquals(KeyUtils.getKey(keyProvider, list1[idx]), KeyUtils.getKey(keyProvider, list2[idx])))
                return false;
        }
        return true;
    }

    public static getKey<T>(keyProvider: ProvidesKey<T>, element: T): object {
        if (!keyProvider) return null;

        return keyProvider.getKey(element);
    }

    static getValueFromKey<T, K>(array: Array<T>, key: K, keyProvider: ProvidesKey<T>) {
        if (array && keyProvider) {
            for (const value of array) {
                if (value && Utils.isEquals(keyProvider.getKey(value), key)) {
                    return value;
                }
            }
        }
        return null;
    }

    static findIndex<T>(
        data: Array<T>,
        item: T,
        keyProvider?: ProvidesKey<T>,
        keyComparator: KeyCompartor = Utils.isEquals,
    ) {
        if (data) {
            const itemKey = KeyUtils.getKey(keyProvider, item);
            return data.findIndex(el => keyComparator(itemKey, KeyUtils.getKey(keyProvider, el)));
        } else {
            return -1;
        }
    }

    /**
     * Take an originalArray, and then add the additionalArray, taking care to replace entries that have the same keys.
     *
     * @param originalArray
     * @param additionalArray
     * @param keyProvider
     * @param keyComparator
     */
    static updateArray<T>(
        originalArray: Array<T>,
        additionalArray: Array<T>,
        keyProvider?: ProvidesKey<T>,
        keyComparator: KeyCompartor = Utils.isEquals,
    ): Array<T> {
        let newData: T[];
        if (additionalArray) {
            if (originalArray) {
                additionalArray = additionalArray.slice(); // copy array
                newData = [];
                for (const el of originalArray) {
                    const idx = KeyUtils.findIndex(additionalArray, el, keyProvider, keyComparator);
                    if (idx === -1) {
                        newData.push(el);
                    } else {
                        newData.push(additionalArray[idx]);
                        additionalArray.splice(idx, 1);
                    }
                }
                newData.push(...additionalArray);
            } else {
                newData = additionalArray;
            }
        } else {
            newData = originalArray;
        }
        return newData;
    }
}
